import { useEffect, useMemo, useState } from 'react';

import { useTheme } from '../../theme/readTheme';
import type { ChartData, ChartFields } from '../../types';
import { Plot } from '../Plot';
import { Empty } from '../states';
import { isNumeric } from '../format';

/**
 * A world map with the countries in the result highlighted.
 *
 * This is Superset's `world_map` viz for App Availability, drawn ourselves so
 * it themes with the rest of the app and needs no ECharts registration. The
 * expected shape is `[country_code, measure]` — the country column is any
 * ISO 3166-1 alpha-2 code (case-insensitive; alpha-3 is accepted too), the
 * measure is optional. When it is present, the fill saturates with magnitude;
 * when it is not, every matching country is filled the same.
 *
 * The map itself is a 110m Natural Earth country polygon set (public/world-110m.geo.json,
 * ~256 kB, one HTTP round trip on first paint, cached forever thereafter). At
 * 110m every country is one path or a small MultiPolygon — enough for a
 * dashboard tile, small enough to serve inline.
 *
 * Projection is equirectangular by design: it is what Superset uses, it is the
 * cheapest to compute, and it is what people mean when they draw a rectangle
 * of world. A globe projection would look sharper and cost the reader nothing
 * for this task (highlight where the app is available) but everything for the
 * one it is not for (compare land areas), and is a change we do not need.
 */

interface Props {
  data: ChartData;
  /** Author's column choices; absent means infer. */
  fields?: ChartFields;
}

interface FeatureProps {
  name: string;
  iso_a2: string | null;
  iso_a3: string | null;
}

interface Feature {
  properties: FeatureProps;
  geometry: {
    type: 'Polygon' | 'MultiPolygon';
    coordinates: number[][][] | number[][][][];
  };
}

interface WorldGeo {
  features: Feature[];
}

/**
 * Loaded once, cached in a module-scoped promise so the second tile on the same
 * dashboard reuses the fetch. React strict-mode double-invoke is fine — the
 * promise resolves once.
 */
let geoPromise: Promise<WorldGeo> | null = null;

const loadGeo = (): Promise<WorldGeo> => {
  if (!geoPromise) {
    geoPromise = fetch('/world-110m.geo.json', { credentials: 'same-origin' })
      .then((res) => {
        if (!res.ok) throw new Error(`Map data ${res.status}`);
        return res.json() as Promise<WorldGeo>;
      })
      .catch((err) => {
        // Let a later tile retry rather than freezing on the first failure.
        geoPromise = null;
        throw err;
      });
  }
  return geoPromise;
};

const VIEW_W = 1000;
const VIEW_H = 500;

/** Equirectangular projection. lon [-180, 180] -> x, lat [-90, 90] -> y (inverted). */
const project = (lon: number, lat: number): [number, number] => [
  ((lon + 180) / 360) * VIEW_W,
  ((90 - lat) / 180) * VIEW_H,
];

/** One polygon ring to an SVG `M x y L x y ...Z` string. */
const ringToPath = (ring: number[][]): string => {
  if (!ring.length) return '';
  const [firstLon, firstLat] = ring[0];
  const [fx, fy] = project(firstLon, firstLat);
  const rest = ring.slice(1).map((pt) => {
    const [x, y] = project(pt[0], pt[1]);
    return `L${x.toFixed(1)} ${y.toFixed(1)}`;
  });
  return `M${fx.toFixed(1)} ${fy.toFixed(1)}${rest.join('')}Z`;
};

const featureToPath = (f: Feature): string => {
  if (f.geometry.type === 'Polygon') {
    return (f.geometry.coordinates as number[][][]).map(ringToPath).join(' ');
  }
  return (f.geometry.coordinates as number[][][][])
    .flatMap((poly) => poly.map(ringToPath))
    .join(' ');
};

/** Look up an ISO A2 / A3 index by column value, ignoring case and whitespace. */
const normaliseCode = (value: unknown): string => String(value ?? '').trim().toUpperCase();

/**
 * Aggregate a query result into per-country totals — the pure data step of
 * this chart, exported and unit-tested (see `test/worldmap.test.ts`).
 *
 * Column detection matches Superset's `world_map` viz: the first non-numeric
 * column is the country code, the first numeric column is the measure.
 * Repeated codes (same country across two months, two genres) sum. Missing
 * or non-string codes are skipped. A row with no numeric measure column
 * scores 1, so `[country]`-only data still draws every listed country.
 *
 * Returns `null` when the result cannot be interpreted (no rows, or no
 * non-numeric column to read a country code from).
 */
export interface WorldMapView {
  byCode: Map<string, number>;
  max: number;
  measureName: string;
}

export const aggregateByCountry = (data: ChartData): WorldMapView | null => {
  if (!data.columns.length || !data.rows.length) return null;

  const numericByIndex = data.columns.map((_, i) =>
    data.rows.slice(0, 20).some((r) => isNumeric(r[i])),
  );
  const countryIndex = data.columns.findIndex(
    (c, i) => !numericByIndex[i] && /country|iso|cc(a?)[23]?/i.test(c),
  );
  const ci = countryIndex >= 0 ? countryIndex : data.columns.findIndex((_, i) => !numericByIndex[i]);
  const mi = data.columns.findIndex((_, i) => numericByIndex[i]);

  if (ci < 0) return null;

  const byCode = new Map<string, number>();
  for (const row of data.rows) {
    const code = normaliseCode(row[ci]);
    if (!code) continue;
    const measure = mi >= 0 && isNumeric(row[mi]) ? row[mi] : 1;
    byCode.set(code, (byCode.get(code) ?? 0) + measure);
  }

  if (byCode.size === 0) return null;

  const max = Math.max(...byCode.values(), 0);
  const measureName = mi >= 0 ? data.columns[mi] : 'Available';

  return { byCode, max, measureName };
};

export const WorldMap = ({ data }: Props) => {
  const theme = useTheme();
  const [geo, setGeo] = useState<WorldGeo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadGeo()
      .then((g) => {
        if (!cancelled) setGeo(g);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const view = useMemo(() => aggregateByCountry(data), [data]);

  if (error) return <Empty message="Could not load the world map data." />;
  if (!geo) return <Empty message="Loading map…" />;
  if (!view) return <Empty message="No country data to plot" />;

  // Sequential ramp — light-end first — so a country with a tiny share still
  // reads as "some" rather than being lost against the base colour.
  const ramp = theme.sequential.length ? theme.sequential : [theme.brand];
  const rampAt = (share: number): string => {
    if (share <= 0) return theme.surfaceSunken;
    const idx = Math.min(
      ramp.length - 1,
      Math.max(0, Math.floor(share * (ramp.length - 1) + 0.5)),
    );
    return ramp[idx];
  };

  const total = Array.from(view.byCode.values()).reduce((a, b) => a + b, 0);
  const highlighted = view.byCode.size;
  const note = `${highlighted.toLocaleString('en-GB')} ${highlighted === 1 ? 'country' : 'countries'}${
    view.max > 1 ? ` · total ${total.toLocaleString('en-GB')}` : ''
  }`;

  return (
    <Plot note={note}>
      <div className="worldmap" role="img" aria-label={`World map: ${highlighted} countries highlighted`}>
        <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} preserveAspectRatio="xMidYMid meet" className="worldmap__svg">
          {/* Ocean background is deliberately the surface, not a blue — the map
              theme is a chart, not a globe. Countries with no data sit one step
              inside the surface so the outline of the world is still visible. */}
          <rect x={0} y={0} width={VIEW_W} height={VIEW_H} fill={theme.surface} />
          {geo.features.map((f, i) => {
            const codeA2 = (f.properties.iso_a2 || '').toUpperCase();
            const codeA3 = (f.properties.iso_a3 || '').toUpperCase();
            const value =
              view.byCode.get(codeA2) ??
              (codeA3 ? view.byCode.get(codeA3) : undefined) ??
              0;
            const share = view.max > 0 ? value / view.max : 0;
            const fill = value > 0 ? rampAt(share) : theme.surfaceSunken;
            const title =
              value > 0
                ? `${f.properties.name}: ${value.toLocaleString('en-GB')} ${view.measureName}`
                : `${f.properties.name}: —`;
            return (
              <path
                key={i}
                d={featureToPath(f)}
                fill={fill}
                stroke={theme.border}
                strokeWidth={0.5}
                vectorEffect="non-scaling-stroke"
              >
                <title>{title}</title>
              </path>
            );
          })}
        </svg>
      </div>
    </Plot>
  );
};
